import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, Copy, Mail, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const TONES = [
    { key: "professional", label: "Professional", hint: "Formal + reassuring" },
    { key: "friendly",     label: "Friendly",     hint: "Warm, uses first name" },
    { key: "apologetic",   label: "Apologetic",   hint: "Owns the issue" },
    { key: "brief",        label: "Brief",        hint: "Under 90 words" },
];

/**
 * Modal that asks Gemini 3 Flash to draft a customer-support reply for a
 * given contact submission. Admin can edit the draft in place, copy it, or
 * open the mailto with subject + body pre-filled.
 */
export default function AIDraftDialog({ submission, onClose }) {
    const [tone, setTone]           = useState("professional");
    const [context, setContext]     = useState("");
    const [busy, setBusy]           = useState(false);
    const [subject, setSubject]     = useState("");
    const [body, setBody]           = useState("");
    const [hasDraft, setHasDraft]   = useState(false);

    const generate = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(
                `/admin/inbox/${submission.id}/draft-reply`,
                { tone, extra_context: context || undefined },
            );
            setSubject(data.subject || "");
            setBody(data.body || "");
            setHasDraft(true);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "AI draft failed. Try again.");
        } finally {
            setBusy(false);
        }
    };

    const copyBoth = () => {
        const text = `Subject: ${subject}\n\n${body}`;
        navigator.clipboard.writeText(text).then(
            () => toast.success("Copied subject + body"),
            () => toast.error("Copy failed"),
        );
    };

    const mailto = `mailto:${submission.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    return (
        <>
            <div
                className="fixed inset-0 bg-black/60 z-[90]"
                onClick={onClose}
                data-testid="ai-draft-overlay"
            />
            <div
                className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[91] w-[92vw] max-w-3xl max-h-[92vh] overflow-y-auto bg-card border border-border rounded-2xl shadow-2xl"
                role="dialog"
                aria-label="AI draft reply"
                data-testid="ai-draft-dialog"
            >
                <div className="p-5 border-b border-border flex items-start justify-between gap-4">
                    <div>
                        <div className="overline text-primary flex items-center gap-1.5">
                            <Sparkles size={12} /> AI reply · Gemini 3 Flash
                        </div>
                        <div className="text-lg font-extrabold tracking-tight">
                            Drafting reply to {submission.name}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                            Original: <span className="italic">"{(submission.message || "").slice(0, 90)}{(submission.message || "").length > 90 ? "…" : ""}"</span>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-md hover:bg-secondary"
                        aria-label="Close"
                        data-testid="ai-draft-close"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="p-5 space-y-5">
                    {/* Tone selector */}
                    <div>
                        <div className="overline text-muted-foreground mb-2">Tone</div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="ai-draft-tones">
                            {TONES.map((t) => (
                                <button
                                    key={t.key}
                                    onClick={() => setTone(t.key)}
                                    disabled={busy}
                                    className={`text-left rounded-lg border p-3 text-sm transition-colors ${
                                        tone === t.key
                                            ? "border-primary bg-primary/10"
                                            : "border-border hover:border-primary/50"
                                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                                    data-testid={`ai-draft-tone-${t.key}`}
                                >
                                    <div className="font-bold">{t.label}</div>
                                    <div className="text-[11px] text-muted-foreground">{t.hint}</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Admin steer / extra context */}
                    <label className="block">
                        <span className="overline text-muted-foreground mb-1 block">
                            Extra context (optional)
                        </span>
                        <textarea
                            rows={2}
                            value={context}
                            onChange={(e) => setContext(e.target.value)}
                            disabled={busy}
                            placeholder="e.g. Payout window is 7 days, not 3. Their onboarding is still pending on their side."
                            className="w-full rounded-lg border border-border bg-transparent px-3 py-2 text-sm outline-none focus:border-primary transition-colors resize-y"
                            data-testid="ai-draft-context"
                        />
                    </label>

                    <div className="flex flex-wrap items-center gap-2">
                        <Button
                            onClick={generate}
                            disabled={busy}
                            className="gap-2"
                            data-testid="ai-draft-generate"
                        >
                            {busy ? <RefreshCw size={14} className="animate-spin" /> : <Sparkles size={14} />}
                            {busy ? "Drafting…" : hasDraft ? "Regenerate" : "Generate reply"}
                        </Button>
                        {hasDraft && !busy && (
                            <span className="text-xs text-muted-foreground">
                                Edit inline below, then copy or open in your mail client.
                            </span>
                        )}
                    </div>

                    {/* Draft output */}
                    {hasDraft && (
                        <div className="space-y-3" data-testid="ai-draft-result">
                            <label className="block">
                                <span className="overline text-primary mb-1 block">Subject</span>
                                <input
                                    type="text"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    className="w-full rounded-lg border border-border bg-transparent px-3 py-2 text-sm font-semibold outline-none focus:border-primary transition-colors"
                                    data-testid="ai-draft-subject"
                                />
                            </label>
                            <label className="block">
                                <span className="overline text-primary mb-1 block">Body</span>
                                <textarea
                                    rows={12}
                                    value={body}
                                    onChange={(e) => setBody(e.target.value)}
                                    className="w-full rounded-lg border border-border bg-transparent px-3 py-2 text-sm leading-relaxed outline-none focus:border-primary transition-colors font-mono"
                                    data-testid="ai-draft-body"
                                />
                            </label>

                            <div className="flex flex-wrap items-center gap-2">
                                <Button variant="outline" size="sm" onClick={copyBoth}
                                        className="gap-1.5" data-testid="ai-draft-copy">
                                    <Copy size={14} /> Copy
                                </Button>
                                <a href={mailto}
                                   className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground text-sm font-extrabold px-4 py-2 rounded-lg hover:opacity-90"
                                   data-testid="ai-draft-mailto">
                                    <Mail size={14} /> Open in mail client
                                </a>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
